import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Market from "@/pages/Market";
import Leaderboard from "@/pages/Leaderboard";
import Results from "@/pages/Results";
import Leagues from "@/pages/Leagues";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Maintenance from "@/pages/Maintenance";
import Regolamento from "@/pages/Regolamento";
import JoinLeague from "@/pages/JoinLeague";
import { PrivacyPolicy, CookiePolicy, Termini } from "@/pages/Legal";
import CookieBanner from "@/components/CookieBanner";

const THEME_VAR_MAP = {
  primary_color: "--brand-primary",
  secondary_color: "--brand-secondary",
  bg_color: "--brand-bg",
  card_color: "--brand-card",
  border_color: "--brand-border",
  input_color: "--brand-input",
  heading_color: "--brand-heading",
  text_color: "--brand-text",
  text_secondary_color: "--brand-text2",
  link_color: "--brand-link",
  cta_color: "--brand-cta",
  cta_hover_color: "--brand-cta-hover",
  btn_secondary_color: "--brand-cta2",
  badge_color: "--brand-badge",
  status_racing_color: "--status-racing",
  status_danger_color: "--status-danger",
  status_success_color: "--status-success",
  status_warning_color: "--status-warning",
};

function applyIconTags(b) {
  if (b.favicon_url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = b.favicon_url;
  }
  if (b.pwa_theme_color) {
    const m = document.querySelector("meta[name='theme-color']");
    if (m) m.setAttribute("content", b.pwa_theme_color);
  }
  if (!b.pwa_icon) return;
  const al = document.getElementById("apple-touch-icon");
  if (al) al.href = b.pwa_icon;
  const manifest = {
    name: "Fantasy Race — 69° Salita Monti Iblei",
    short_name: "FantasyRace",
    description: "Il fantasy game della 69° Salita dei Monti Iblei — Chiaramonte Gulfi (RG)",
    start_url: "/",
    scope: "/",
    display: "standalone",
    orientation: "portrait",
    background_color: b.pwa_theme_color || "#121214",
    theme_color: b.pwa_theme_color || "#121214",
    icons: [
      { src: b.pwa_icon, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: b.pwa_icon, sizes: "512x512", type: "image/png", purpose: "any maskable" },
    ],
  };
  const link = document.getElementById("pwa-manifest");
  if (link) link.href = URL.createObjectURL(new Blob([JSON.stringify(manifest)], { type: "application/json" }));
}

function useThemeEngine(settings) {
  useEffect(() => {
    const b = settings?.branding;
    if (!b) return;
    const root = document.documentElement;
    Object.entries(THEME_VAR_MAP).forEach(([k, cssVar]) => {
      if (b[k]) root.style.setProperty(cssVar, b[k]);
    });
    if (b.hero_gradient && b.gradient_from && b.gradient_to) {
      document.body.style.background = `linear-gradient(160deg, ${b.gradient_from}, ${b.gradient_to})`;
      document.body.style.backgroundAttachment = "fixed";
    } else if (b.bg_color) {
      document.body.style.background = b.bg_color;
    }
    if (b.text_color) document.body.style.color = b.text_color;
    if (b.font_family) document.body.style.fontFamily = `'${b.font_family}', sans-serif`;
    document.body.classList.toggle("theme-sharp", b.border_style === "sharp");
    document.body.classList.toggle("theme-pill", b.border_style === "pill");
    document.body.classList.remove("card-minimal", "card-shadow", "card-neon", "card-glass");
    if (b.card_style) document.body.classList.add(`card-${b.card_style}`);
    applyIconTags(b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#121214] flex items-center justify-center">
      <span className="font-racing font-black text-[#FF1E27] tracking-widest animate-pulse">
        FANTASYRACE
      </span>
    </div>
  );
}

const PUBLIC_PATHS = ["/login", "/reset-password", "/privacy", "/cookie-policy", "/termini"];

function Gate({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
    refetchInterval: 15000,
  });
  useThemeEngine(settings);

  if (!ready) return <LoadingScreen />;

  if (settings?.maintenance?.active && user?.role !== "admin" && !PUBLIC_PATHS.includes(location.pathname)) {
    return <Maintenance settings={settings} />;
  }
  if (!user && !PUBLIC_PATHS.includes(location.pathname)) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (user && location.pathname === "/login") {
    const dest = new URLSearchParams(location.search).get("next");
    return <Navigate to={dest || "/"} replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/termini" element={<Termini />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/mercato" element={<Market />} />
              <Route path="/classifica" element={<Leaderboard />} />
              <Route path="/risultati" element={<Results />} />
              <Route path="/leghe" element={<Leagues />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/regolamento" element={<Regolamento />} />
              <Route path="/admin" element={<Admin />} />
            </Route>
            <Route path="/join-league" element={<JoinLeague />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Gate>
        <CookieBanner />
        <Toaster position="bottom-right" theme="dark" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
